import React, { useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import * as actions from "../../_redux/level-sections/LevelSectionsAction";
import {
  DeleteButton,
  ExportButton,
  FormButton,
  copy,
  deletionAlert,
  exportToExcel,
  exportToPDF,
  getAuthorities,
  noRecordsAlert,
  print,
  redirectToPages,
  resultAlert
} from "../../../../../_helpers/Helpers";
import { 
  Card, 
  CardBody, 
  CardHeader,
  CardHeaderToolbar,
  ModalProgressBar
} from "../../../../../_metronic/_partials/controls";
import { LevelSectionsFilter } from "./level-sections-filter/LevelSectionsFilter";
import { LevelSectionsTable } from "./level-sections-table/LevelSectionsTable";
import { useLevelSectionsUIContext } from "./LevelSectionsUIContext";

export function LevelSectionsCard() {
  // LevelSections UI Context
  const levelSectionsUIContext = useLevelSectionsUIContext();
  const levelSectionsUIProps = useMemo(() => {
    return {
      ids: levelSectionsUIContext.ids,
      setIds: levelSectionsUIContext.setIds,
      queryParams: levelSectionsUIContext.queryParams,
      openNewLevelSectionPage: levelSectionsUIContext.openNewLevelSectionPage,
    };
  }, [levelSectionsUIContext]);
    
  // Getting curret state of Level Section list from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.levelSections }),
    shallowEqual
  );
  const { actionsLoading, isSuccess, type, authorities } = currentState;

  // Authority Parameters
  const { isExportationAuthorized, isAdditionAuthorized, isDeletionAuthorized } = getAuthorities(authorities);

  // Sections Redux state
  const dispatch = useDispatch();

  // Kimlik, Yetki ve Hata Kontrolleri
  if (isSuccess === false) return redirectToPages(type);

  /** Belli Bir Kur Sınıfı Listesini Excele Aktarır */
  const exportSectionsToExcel = () => {
    dispatch(actions.getAllLevelSections(levelSectionsUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const widths = [15, 15, 30, 15, 30];
      const columns = ["Sınıf Düzeyi", "Kur Sınıfı", "Kur", "Kapasite", "Ders"];
      entities.forEach(entity => rows.push([entity.gradeLevelName, entity.grade, entity.name, entity.capacity, entity.courseName]));
      const filename = "Kur Sınıfı Listesi";
      exportToExcel({ columns, rows, widths, filename });
    });    
  }

  /** Belli Bir Kur Sınıfı Listesini PDF e Aktarır */
  const exportSectionsToPDF = () => {
    dispatch(actions.getAllLevelSections(levelSectionsUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const views = [ 
        {
          header: "Sınıf Düzeyi",
          rows: entities.map(entity => entity.gradeLevelName)
        },
        {
          header: "Kur Sınıfı",
          rows: entities.map(entity => entity.grade)
        },
        {
          header: "Kur",
          rows: entities.map(entity => entity.name)
        },
        {
          header: "Kapasite",
          rows: entities.map(entity => entity.capacity)
        },
        {
          header: "Ders",
          rows: entities.map(entity => entity.courseName)
        }
      ];

      const filename = "Kur Sınıfı Listesi";
      exportToPDF(views, filename);
    });
  }

  /** Belli Bir Kur Sınıfı Listesini Yazdırma Penceresini Açar */
  const printSections = () => {
    dispatch(actions.getAllLevelSections(levelSectionsUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const data = [];    
      const header = "Kur Sınıfı Listesi";
      const properties = ["Sınıf Düzeyi", "Kur Sınıfı", "Kur", "Kapasite", "Ders"];

      entities.forEach(entity => {
        data.push({
          "Sınıf Düzeyi": entity.gradeLevelName,
          "Kur Sınıfı": entity.grade,
          "Kur": entity.name,
          "Kapasite": entity.capacity,
          "Ders": entity.courseName
        });
      });

      print({ data, properties, header });
    });
  }

  /** Belli Bir Kur Sınıfı Listesini Panoya Kopyalar */
  const copySections = () => {
    dispatch(actions.getAllLevelSections(levelSectionsUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const header = "Kur Sınıfı Listesi";
      const columns = ["Sınıf Düzeyi", "Kur Sınıfı", "Kur", "Kapasite", "Ders"];
      entities.forEach(entity => rows.push([entity.gradeLevelName, entity.grade, entity.name, entity.capacity, entity.courseName]));
      copy({ header, columns, rows });
    });
  }

  /** Belli Bir Kur Sınıfı Listesini Siler */
  const deleteLevelSections = (ids) => {
    if (typeof ids === 'string') ids = ids.split(",");
  
    if (ids.length < 1) {
      noRecordsAlert();        
      return;
    }  
    deletionAlert().then(result => {
      if(result.isConfirmed) dispatch(actions.deleteLevelSections(ids)).then(result => onResult(result));
    });
  }

  const onResult = ({ isSuccess, type, message }) => {
    resultAlert(type, message);
  
    if (isSuccess) {
      setTimeout(() => {
        // clear selections list
        levelSectionsUIProps.setIds([]);
        // refresh list after deletion
        dispatch(actions.getLevelSections(levelSectionsUIProps.queryParams));        
      }, 2500);
    }
  };
  
  const title = <>Kur Sınıfı Listesi{levelSectionsUIProps.ids.length > 0 ?
                  <> [Seçilen Kayıt Sayısı: <span style={{color:"red"}}>{levelSectionsUIProps.ids.length}</span>]</> :
                  ""}
                </>;

  return (
    <Card>
      {actionsLoading && <ModalProgressBar />}
      <CardHeader title={title}>
        {
          (isExportationAuthorized || isAdditionAuthorized || isDeletionAuthorized) &&
          <CardHeaderToolbar>
            {
              isExportationAuthorized &&
              <ExportButton
                exportToExcel={exportSectionsToExcel}
                exportToPDF={exportSectionsToPDF}
                print={printSections}
                copy={copySections}
              />
            }
            {
              isAdditionAuthorized && 
              <FormButton
                path="Navigation/Plus"
                title="Yeni Kur Sınıfı"
                method={levelSectionsUIProps.openNewLevelSectionPage}
              />
            }              
            {
              isDeletionAuthorized &&              
              <DeleteButton
                title="Seçilenleri Sil"
                method={() => deleteLevelSections(levelSectionsUIProps.ids)}
              />
            }
          </CardHeaderToolbar>
        }        
      </CardHeader>
      <CardBody>
        <LevelSectionsFilter />
        <LevelSectionsTable
          deleteLevelSections={deleteLevelSections}
        />
      </CardBody>
    </Card>
  );
}